import { useEffect, useRef } from "react";

export type ShortcutHandler = (event: KeyboardEvent) => void;
export type ShortcutMap = Record<string, ShortcutHandler>;

/** Typing in these should never trigger a single-letter shortcut. */
function isTextEntry(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return (
    el.isContentEditable ||
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT"
  );
}

/**
 * True while a modal <dialog> is open — the shortcuts help or the nav drawer.
 *
 * Those handle their own keys: Escape closes them, Enter activates the focused
 * control. Global shortcuts must stand down while one is up, or they swallow
 * those keys via preventDefault — which is how the shortcuts dialog ended up
 * unable to be closed from the keyboard.
 */
function modalIsOpen(): boolean {
  return document.querySelector("dialog[open]") !== null;
}

/**
 * Normalizes an event into a lookup key: lower-cased `event.key`, prefixed
 * with modifiers, e.g. "a", "arrowright", "shift+?", "mod+k".
 */
function keyOf(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push("mod");
  if (e.altKey) parts.push("alt");
  parts.push(e.key.toLowerCase());
  return parts.join("+");
}

/**
 * Binds global keyboard shortcuts. Handlers are read through a ref so callers
 * can pass a fresh object each render without rebinding the listener.
 *
 * Escape and Enter still fire while a text field is focused; letter and digit
 * keys do not, so numeric answers and the search box behave normally.
 */
export function useKeyboard(map: ShortcutMap, enabled = true) {
  const ref = useRef(map);
  ref.current = map;

  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(e: KeyboardEvent) {
      if (modalIsOpen()) return;

      const key = keyOf(e);
      const handler = ref.current[key];
      if (!handler) return;

      const alwaysAllowed = key === "escape" || key === "enter" || key.startsWith("mod+");
      if (isTextEntry(e.target) && !alwaysAllowed) return;

      e.preventDefault();
      handler(e);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled]);
}

/** Shortcut reference shown in the help dialog, in display order. */
export const SHORTCUTS = [
  { keys: ["1", "2", "3"], label: "scToggleAnswer" as const, arg: "n" },
  { keys: ["Enter"], label: "scCheck" as const },
  { keys: ["→", "N"], label: "scNext" as const },
  { keys: ["←", "P"], label: "scPrev" as const },
  { keys: ["T"], label: "scTranslate" as const },
  { keys: ["E"], label: "scExplain" as const },
  { keys: ["V"], label: "scVideo" as const },
  { keys: ["M"], label: "scMark" as const },
  { keys: ["S"], label: "scSwapLang" as const },
  { keys: ["L"], label: "scCycleSecondary" as const },
  { keys: ["F"], label: "scFinish" as const },
  { keys: ["Esc"], label: "scHome" as const },
  { keys: ["?"], label: "scHelp" as const },
];
